import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { consume } from '@lit/context';
import { nsStripeServiceContext } from '../../../../ns-services/contexts.js';

import { nsLayoutStyles } from '../../../../ns-styles/index.js';

import { NSStripeService } from '../../../../ns-services/ns-stripe-service.js';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';
import { callLambda } from '../../../../helpers/lambda.js';
import Subscription from '../../../../models/subscription.js';
import '../../../../ns-elements/ns-job-layout.js';
import { renderDate, renderMoney } from '../../../../helpers/format-render.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import definition from '@job-schemas/subscription/update-plan.json';
import { jobSchema } from '../../job-schema.js';

enum JobStatus {
  UPDATING_STAGE,
  PAYMENT_STAGE,
  UPDATED,
}

@customElement(NSUpdateSubscriptionPlan.is)
export default class NSUpdateSubscriptionPlan extends NSJobComponent {
  static get is() {
    return 'ns-update-subscription-plan';
  }

  @consume({ context: nsStripeServiceContext })
  private __stripe?: NSStripeService;

  @state()
  private __subscription?: Subscription;

  @state()
  private __newSubscription?: Subscription;

  @state()
  private __jobStatus: JobStatus = JobStatus.UPDATING_STAGE;

  private __clientSecret?: string;

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'hidden' } } });
  }

  static styles = [nsLayoutStyles, ...super.styles];

  async connectedCallback() {
    await super.connectedCallback();

    if (!this._db) throw new Error('Unable to find db service');

    if (!this._auth) throw new Error('Unable to find auth service');

    if (!this.__stripe) throw new Error('Unable to find stripe service');

    if (!this._job) throw new Error('Unable to find job');

    if (!this._directory) throw new Error('Directory service not found');
    if (!this._directory.me || !this._directory.me.companyId)
      throw new Error('Company id not found in the directory service');

    this.__subscription = await this.loadInput('subscription', 'subscriptionId');

    if (this.__subscription.status !== 'ACTIVE') {
      const queryCall = await this._db.query('subscription', {
        id: {
          $not: this._job.inputValues.subscriptionId,
        },
        $or: [
          {
            status: {
              $eq: 'PROCESSING',
            },
          },
          {
            status: {
              $eq: 'ACTIVE',
            },
          },
        ],
      });

      const subscriptions = queryCall.results as unknown as Subscription[];
      if (subscriptions.length < 1) throw new Error('Could not find any subscriptions linked to your company');
      this.__newSubscription = subscriptions.find((s) => s.status === 'ACTIVE' && s.plan !== this.__subscription?.plan);
      this.__newSubscription = this.__newSubscription
        ? this.__newSubscription
        : subscriptions.find((s) => s.status === 'PROCESSING' && s.plan !== this.__subscription?.plan);
    }

    const jobMetadata = this._job.metadata.find((m) => m.key === 'CONFIRM_PAYMENT_SETUP');
    if (jobMetadata && jobMetadata.value === 'CONFIRMED') this.__jobStatus = JobStatus.UPDATED;

    if (this.__clientSecret) await this.__stripe.mountStripeElement(this, this.__clientSecret);
  }

  disconnectedCallback() {
    this.__stripe?.cleanElements();

    super.disconnectedCallback();
  }

  private async __update() {
    this.clearError();
    if (!this._db) throw new Error('Unable to find db service');
    if (!this.__stripe) throw new Error('Unable to find stripe service');
    if (!this._directory || !this._directory.me) throw new Error('Missing directory service');
    if (!this._auth) throw new Error('Missing auth service');
    if (!this.__subscription) {
      this.showError('Unable to find a subscription to be updated');
      throw new Error('Can not find subscription to be updated');
    }

    try {
      this.loading('Updating Subscription Plan');
      const output = await callLambda(
        'subscription/update-plan',
        'POST',
        this._auth.token,
        JSON.stringify({
          subscriptionId: this.__subscription.id,
          command: this.__subscription.plan === 'MONTHLY' ? 'UPGRADE' : 'DOWNGRADE',
        }),
      );

      this.loaded();
      const res = output.res as unknown as { subscriptionId: string; clientSecret: string };
      if (!res.clientSecret) throw new Error('Did not receive a client secret back from the lambda');
      this.__jobStatus = JobStatus.PAYMENT_STAGE;
      this.__subscription = await this._db.getById('subscription', res.subscriptionId);
      this.__clientSecret = res.clientSecret;
      await this.__stripe.mountStripeElement(this, this.__clientSecret);
    } catch (err: any) {
      this.loaded();
      this.showError(err, 'Something went wrong');
      throw new Error(err.message);
    }
  }

  private async __confirmPaymentSetup() {
    if (!this._db) throw new Error('[__confirmPaymentSetup] Unable to find db service');
    if (!this.__stripe) throw new Error('Unable to find stripe service');
    if (!this._job) throw new Error('[__confirmPaymentSetup] Unable to find job');
    if (!this.__subscription) throw new Error('[__confirmPaymentSetup] Unable to find subscription');

    this.loading('Processing Payment');
    await this.__stripe.confirmPaymentSetup(this.__subscription.id, this._job.id);
    this.loaded();
  }

  render() {
    const plan = this.__subscription ? this.__subscription.plan : 'N/A';
    const amount = this.__subscription
      ? renderMoney(this.__subscription.billing.amount, this.__subscription.billing.currency)
      : 'N/A';
    const renewalDate = renderDate(this.__subscription?.renewalDate, 'longDate', 'N/A');
    return html`
      <ns-job-layout>
        ${choose(
          this.__jobStatus,
          [
            [
              JobStatus.UPDATING_STAGE,
              () => html`
                <div class="job-panel">
                  <p>
                    Your current plan is on a ${plan.toLowerCase()} basis, with a price of ${amount}. The next renewal
                    date for your subscription is on ${renewalDate}.
                  </p>
                </div>
                <div slot="aside" class="primary-info">
                  <span class="info-icon" aria-hidden="true">i</span>
                  <p>
                    If you ${plan === 'MONTHLY' ? 'upgrade' : 'downgrade'} to a
                    ${plan === 'MONTHLY' ? 'yearly' : 'monthly'} subscription, you'll be billed now, and it will begin
                    on ${renewalDate} once your current subscription ends. You will have to confirm the first payment
                    for the new ${plan === 'MONTHLY' ? 'yearly' : 'monthly'} subscription.
                  </p>
                  <p>
                    Please ensure that your payment information is up to date to avoid any interruptions in service.
                  </p>
                  <p class="note">
                    If you have any questions or need assistance with your account, feel free to reach out to our
                    support team.
                  </p>
                </div>
              `,
            ],
            [
              JobStatus.PAYMENT_STAGE,
              () => html`
                <div class="job-panel">
                  <slot name="stripe-payment-form"></slot>
                </div>
              `,
            ],
            [
              JobStatus.UPDATED,
              () => html`
                <div class="job-panel">
                  <p>You have successfully updated your subscription plan. Your renewal date is ${renewalDate}</p>
                </div>
              `,
            ],
          ],
          () => html`
            <div class="page-not-found">404</div>
          `,
        )}
      </ns-job-layout>
    `;
  }

  renderActions() {
    const plan = this.__subscription ? this.__subscription.plan : 'N/A';
    switch (this.__jobStatus) {
      case JobStatus.UPDATING_STAGE:
        return html`
          <wa-button variant="brand" appearance="outlined" type="button" @click=${() => this.outcome('complete')}>
            Cancel
          </wa-button>
          ${plan !== 'YEARLY'
            ? html`
                <wa-button variant="brand" type="button" @click=${() => this.__update()}>Upgrade Plan</wa-button>
              `
            : nothing}
          ${plan !== 'MONTHLY'
            ? html`
                <wa-button variant="brand" type="button" @click=${() => this.__update()}>Downgrade Plan</wa-button>
              `
            : nothing}
        `;
      case JobStatus.PAYMENT_STAGE:
        return html`
          <wa-button variant="brand" type="button" @click=${() => this.__confirmPaymentSetup()}>Confirm</wa-button>
        `;
      case JobStatus.UPDATED:
        return html`
          <wa-button variant="brand" type="button" @click=${() => this.outcome('complete')}>Complete</wa-button>
        `;
      default:
        return nothing;
    }
  }
}
